(() => {
  const materials = {
    ceramic: { front: [0.96, 0.95, 0.91], back: [0.93, 0.92, 0.88], edge: [0.86, 0.84, 0.79], shine: 0.20 },
    wood: { front: [0.82, 0.68, 0.48], back: [0.76, 0.61, 0.41], edge: [0.62, 0.47, 0.30], shine: 0.025 },
    mdf: { front: [0.84, 0.77, 0.65], back: [0.70, 0.58, 0.43], edge: [0.61, 0.49, 0.35], shine: 0.025 },
    acrylic: { front: [0.93, 0.96, 0.97], back: [0.82, 0.88, 0.91], edge: [0.73, 0.82, 0.86], shine: 0.24 },
    glass: { front: [0.92, 0.96, 0.97], back: [0.80, 0.89, 0.91], edge: [0.65, 0.79, 0.79], shine: 0.30 },
    aluminum: { front: [0.91, 0.92, 0.92], back: [0.69, 0.71, 0.72], edge: [0.57, 0.60, 0.62], shine: 0.25 },
    plastic: { front: [0.93, 0.95, 0.96], back: [0.85, 0.88, 0.90], edge: [0.77, 0.81, 0.83], shine: 0.18 }
  };
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  class VenroOrnamentPlasticStar3D extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.dataset.referenceOnly = 'true';
      this.dataset.modelFidelity = 'supplier-design-outline-domed-shell-estimate';
      this.dataset.opticalFidelity = 'estimated';
      this.canvas = this.querySelector('canvas');
      this.abort = new AbortController();
      this.yaw = -0.22;
      this.pitch = 0.12;
      this.zoom = 0.40;
      this.pointers = new Map();
      if (!this.canvas) { this.fail('missing-canvas'); return; }
      const on = (element, type, handler, options = {}) => element?.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      on(this.querySelector('[data-model-front]'), 'click', () => { this.yaw = 0; this.pitch = 0; this.draw(); });
      on(this.querySelector('[data-model-back]'), 'click', () => { this.yaw = Math.PI; this.pitch = 0; this.draw(); });
      on(this.querySelector('[data-model-side]'), 'click', () => { this.yaw = 1.38; this.pitch = 0.16; this.draw(); });
      on(this.querySelector('[data-model-reset]'), 'click', () => { this.yaw = -0.22; this.pitch = 0.12; this.zoom = 0.40; this.draw(); });
      on(this.querySelector('[data-model-in]'), 'click', () => this.changeZoom(1.18));
      on(this.querySelector('[data-model-out]'), 'click', () => this.changeZoom(1 / 1.18));
      on(this.canvas, 'keydown', event => {
        const turns = { ArrowLeft: [-0.15, 0], ArrowRight: [0.15, 0], ArrowUp: [0, -0.12], ArrowDown: [0, 0.12] };
        if (turns[event.key]) {
          event.preventDefault();
          this.yaw += turns[event.key][0];
          this.pitch = clamp(this.pitch + turns[event.key][1], -1.3, 1.3);
          this.draw();
        } else if (event.key === '+' || event.key === '=') { event.preventDefault(); this.changeZoom(1.1); }
        else if (event.key === '-') { event.preventDefault(); this.changeZoom(1 / 1.1); }
      });
      on(this.canvas, 'pointerdown', event => {
        this.canvas.focus({ preventScroll: true });
        this.canvas.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, [event.clientX, event.clientY]);
      });
      on(this.canvas, 'pointermove', event => {
        const previous = this.pointers.get(event.pointerId);
        if (!previous) return;
        const next = [event.clientX, event.clientY];
        if (this.pointers.size === 1) {
          this.yaw += (next[0] - previous[0]) * 0.012;
          this.pitch = clamp(this.pitch + (next[1] - previous[1]) * 0.01, -1.3, 1.3);
        } else {
          const other = [...this.pointers.entries()].find(([id]) => id !== event.pointerId)?.[1];
          if (other) {
            const oldDistance = Math.hypot(previous[0] - other[0], previous[1] - other[1]);
            const newDistance = Math.hypot(next[0] - other[0], next[1] - other[1]);
            if (oldDistance > 5) this.zoom = clamp(this.zoom * newDistance / oldDistance, 0.35, 2.4);
          }
        }
        this.pointers.set(event.pointerId, next);
        this.draw();
      });
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) on(this.canvas, type, event => this.pointers.delete(event.pointerId));
      on(this.canvas, 'wheel', event => {
        if (document.activeElement !== this.canvas) return;
        event.preventDefault();
        this.changeZoom(Math.exp(-event.deltaY * 0.001));
      }, { passive: false });
      on(this.canvas, 'webglcontextlost', event => { event.preventDefault(); this.contextLost = true; this.fail('webgl-context-lost'); });
      on(this.canvas, 'webglcontextrestored', () => {
        this.contextLost = false;
        this.gl = null;
        this.meshKey = null;
        this.uploadedSource = null;
        this.uploadedVersion = null;
      });
      this.resize = new ResizeObserver(() => this.draw());
      this.resize.observe(this.canvas);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      if (this.gl) {
        this.gl.deleteBuffer(this.buffer);
        this.gl.deleteTexture(this.texture);
        this.gl.deleteTexture(this.referenceTexture);
        this.gl.deleteProgram(this.program);
      }
      this.gl = null;
      this.buffer = null;
      this.texture = null;
      this.referenceTexture = null;
      this.referenceImage = null;
      this.maskImage = null;
      this.program = null;
      this.meshKey = null;
      this.uploadedSource = null;
      this.uploadedVersion = null;
      this.spec = null;
      this.failed = false;
      this.contextLost = false;
      this.bound = false;
      this.pointers?.clear();
    }

    changeZoom(factor) { this.zoom = clamp(this.zoom * factor, 0.35, 2.4); this.draw(); }

    fail(reason) {
      this.dataset.ready = 'false';
      this.dataset.error = reason;
      this.spec = null;
      if (this.gl) { this.gl.clearColor(0, 0, 0, 0); this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT); }
      const fallback = this.querySelector('[data-model-fallback]');
      if (fallback) fallback.hidden = false;
      this.dispatchEvent(new CustomEvent('model-unavailable', { bubbles: true, detail: { reason, referenceOnly: true } }));
    }

    show(source, specification, version) {
      const shape = String(specification?.shape || '').toLowerCase();
      const material = String(specification?.material || '').toLowerCase();
      const widthCm = Number(specification?.widthCm);
      const heightCm = Number(specification?.heightCm);
      const thicknessMm = Number(specification?.thicknessMm);
      const evidence = specification?.source || specification?.evidence;
      const referenceImage = specification?.referenceImage;
      const maskImage = specification?.maskImage;
      const optical = specification?.opticalAppearance;
      const opacities = [optical?.frontOpacity, optical?.backOpacity, optical?.edgeOpacity].map(Number);
      if (!source || !referenceImage || !maskImage || specification?.flatGeometryConfirmed !== true || specification?.silhouetteVerified !== true || specification?.referenceSurfaceBlank !== true || optical?.basis !== 'visual-estimate' || typeof optical?.description !== 'string' || !optical.description.trim() || opacities.some(v => !Number.isFinite(v) || v <= 0 || v >= 1) || !/^[a-z0-9_-]{1,40}$/.test(shape) || (material !== 'plastic' || shape !== 'star' || specification?.supplierProduct !== 'P01582' || specification?.supplierSku !== 'P01582:size:0' || widthCm !== 9 || heightCm !== 9 || thicknessMm !== 20 || specification?.allowNoVisibleHole !== true) ||
          !Number.isFinite(widthCm) || !Number.isFinite(heightCm) || !Number.isFinite(thicknessMm) ||
          widthCm <= 0 || heightCm <= 0 || thicknessMm <= 0 || thicknessMm >= Math.min(widthCm, heightCm) * 10 ||
          typeof evidence !== 'string' || !evidence.trim()) {
        this.fail('unverified-dimensions-or-source');
        return false;
      }
      if ((material === 'glass' || material === 'acrylic') && !specification.maskImage) { this.fail('transparent-material-mask-required'); return false; }
      if (!this.bound || !this.canvas) { this.fail('not-connected'); return false; }
      if (this.contextLost) { this.fail('webgl-context-lost'); return false; }
      try {
        if (!this.gl) this.init();
        this.spec = { shape, material, widthCm, heightCm, thicknessMm, source: evidence, opticalAppearance: { frontOpacity: opacities[0], backOpacity: opacities[1], edgeOpacity: opacities[2], basis: optical.basis, description: optical.description } };
        const key = `${shape}:${material}:${widthCm}:${heightCm}:${thicknessMm}:${evidence}`;
        if (this.meshKey !== key || this.referenceImage !== referenceImage || this.maskImage !== maskImage) {
          const gl = this.gl;
          const geometry = this.referenceGeometry(referenceImage, maskImage, specification);
          const vertices = this.mesh(this.spec, geometry);
          gl.activeTexture(gl.TEXTURE1);
          gl.bindTexture(gl.TEXTURE_2D, this.referenceTexture);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, geometry.texture);
          gl.activeTexture(gl.TEXTURE0);
          this.referenceImage = referenceImage;
          this.maskImage = maskImage;
          this.dataset.holes = String(geometry.holes);
          this.dataset.maskResolution = String(geometry.size);
          gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
          gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
          this.count = vertices.length / 9;
          this.meshKey = key;
        }
        if (version == null || this.uploadedSource !== source || this.uploadedVersion !== String(version)) {
          const gl = this.gl;
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, this.texture);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
          this.uploadedSource = source;
          this.uploadedVersion = String(version);
        }
        Object.assign(this.dataset, { ready: 'true', shape, material, widthCm: String(widthCm), heightCm: String(heightCm), thicknessMm: String(thicknessMm), designVersion: String(version) });
        delete this.dataset.error;
        const fallback = this.querySelector('[data-model-fallback]');
        if (fallback) fallback.hidden = true;
        this.draw();
        return true;
      } catch (error) {
        this.fail(error?.message || 'webgl-or-texture-unavailable');
        return false;
      }
    }

    init() {
      const gl = this.canvas.getContext('webgl', { alpha: true, antialias: true, preserveDrawingBuffer: true });
      if (!gl) throw new Error('WebGL unavailable');
      this.gl = gl;
      const vertex = `precision mediump float;attribute vec3 aPosition;attribute vec3 aNormal;attribute vec2 aUV;attribute float aSurface;uniform vec2 uAngles;uniform float uDistance;uniform float uAspect;varying vec3 vNormal;varying vec3 vPosition;varying vec2 vUV;varying float vSurface;
      vec3 rotate(vec3 p){float c=cos(uAngles.x),s=sin(uAngles.x),cx=cos(uAngles.y),sx=sin(uAngles.y);vec3 q=vec3(c*p.x+s*p.z,p.y,-s*p.x+c*p.z);return vec3(q.x,cx*q.y-sx*q.z,sx*q.y+cx*q.z);}
      void main(){vec3 p=rotate(aPosition);vNormal=rotate(aNormal);vPosition=p;vUV=aUV;vSurface=aSurface;p.y-=.70;p.z-=uDistance;gl_Position=vec4(p.x*3.4/uAspect,p.y*3.4,-1.002002*p.z-.2002002,-p.z);}`;
      const fragment = `precision mediump float;varying vec3 vNormal;varying vec3 vPosition;varying vec2 vUV;varying float vSurface;uniform sampler2D uTexture;uniform sampler2D uReferenceTexture;uniform vec3 uFront;uniform vec3 uBack;uniform vec3 uEdge;uniform float uShine;uniform float uDistance;uniform vec3 uOpacity;
      void main(){vec3 n=normalize(vNormal),l=normalize(vec3(-.4,.65,1.5)),eye=normalize(vec3(0.,0.,uDistance)-vPosition);float light=.68+.32*max(dot(n,l),0.);float spec=pow(max(dot(n,normalize(l+eye)),0.),65.)*uShine;vec3 col;float opacity;if(vSurface<.5){vec4 ink=texture2D(uTexture,vUV);vec4 reference=texture2D(uReferenceTexture,vUV);vec3 base=uFront;ink.a*=reference.a;opacity=uOpacity.x+(1.-uOpacity.x)*ink.a;col=(base*uOpacity.x*(1.-ink.a)+ink.rgb*ink.a)/max(opacity,.00001);}else if(vSurface<1.5){col=uBack;opacity=uOpacity.y;}else if(vSurface<2.5){col=uEdge;opacity=uOpacity.z;}else{col=vec3(.78,.80,.81);opacity=1.;}gl_FragColor=vec4(col*light+vec3(spec),opacity);}`;
      const shader = (type, text) => {
        const result = gl.createShader(type);
        gl.shaderSource(result, text);
        gl.compileShader(result);
        if (!gl.getShaderParameter(result, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(result));
        return result;
      };
      const vs = shader(gl.VERTEX_SHADER, vertex), fs = shader(gl.FRAGMENT_SHADER, fragment);
      this.program = gl.createProgram();
      gl.attachShader(this.program, vs);
      gl.attachShader(this.program, fs);
      gl.linkProgram(this.program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) throw new Error('Shader link');
      gl.useProgram(this.program);
      this.uniform = {};
      for (const name of ['uAngles', 'uDistance', 'uAspect', 'uTexture', 'uReferenceTexture', 'uFront', 'uBack', 'uEdge', 'uShine', 'uOpacity']) this.uniform[name] = gl.getUniformLocation(this.program, name);
      this.buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
      let offset = 0;
      for (const [name, size] of [['aPosition', 3], ['aNormal', 3], ['aUV', 2], ['aSurface', 1]]) {
        const location = gl.getAttribLocation(this.program, name);
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, size, gl.FLOAT, false, 36, offset * 4);
        offset += size;
      }
      this.texture = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.uniform1i(this.uniform.uTexture, 0);
      this.referenceTexture = gl.createTexture();
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this.referenceTexture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.uniform1i(this.uniform.uReferenceTexture, 1);
      gl.activeTexture(gl.TEXTURE0);
      gl.enable(gl.DEPTH_TEST);
      gl.enable(gl.CULL_FACE);
      gl.clearColor(0, 0, 0, 0);
    }

    referenceGeometry(image, maskImage, specification) {
      const imageWidth = image.naturalWidth || image.videoWidth || image.width;
      const imageHeight = image.naturalHeight || image.videoHeight || image.height;
      const maskWidth = maskImage.naturalWidth || maskImage.videoWidth || maskImage.width;
      const maskHeight = maskImage.naturalHeight || maskImage.videoHeight || maskImage.height;
      if (!(imageWidth > 0 && imageHeight > 0) || imageWidth !== maskWidth || imageHeight !== maskHeight) throw new Error('aligned-reference-and-mask-required');
      const scanSize = 512, probe = document.createElement('canvas');
      probe.width = scanSize; probe.height = scanSize;
      const probeContext = probe.getContext('2d', { willReadFrequently: true });
      probeContext.drawImage(maskImage, 0, 0, scanSize, scanSize);
      const pixels = probeContext.getImageData(0, 0, scanSize, scanSize).data;
      let minX = scanSize, minY = scanSize, maxX = -1, maxY = -1, solid = 0, transparent = 0;
      for (let y = 0; y < scanSize; y++) for (let x = 0; x < scanSize; x++) {
        const alpha = pixels[(y * scanSize + x) * 4 + 3];
        if (alpha >= 160) { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); solid++; }
        if (alpha < 16) transparent++;
      }
      if (solid < scanSize * scanSize * 0.02 || transparent < scanSize * scanSize * 0.02 || maxX <= minX || maxY <= minY) throw new Error('transparent-silhouette-required');
      const texture = document.createElement('canvas');
      const size = 384;
      texture.width = size; texture.height = size;
      const ctx = texture.getContext('2d');
      ctx.drawImage(image, minX * imageWidth / scanSize, minY * imageHeight / scanSize, (maxX - minX + 1) * imageWidth / scanSize, (maxY - minY + 1) * imageHeight / scanSize, 0, 0, size, size);
      const sampledMask = document.createElement('canvas'); sampledMask.width = size; sampledMask.height = size;
      const maskContext = sampledMask.getContext('2d', { willReadFrequently: true });
      maskContext.drawImage(probe, minX, minY, maxX - minX + 1, maxY - minY + 1, 0, 0, size, size);
      const rgba = maskContext.getImageData(0, 0, size, size).data;
      const occupied = new Uint8Array(size * size);
      for (let i = 0; i < occupied.length; i++) occupied[i] = rgba[4 * i + 3] >= 160 ? 1 : 0;
      const visited = new Uint8Array(occupied.length), queue = new Int32Array(occupied.length);
      let largest = [], largestCount = 0;
      for (let start = 0; start < occupied.length; start++) {
        if (!occupied[start] || visited[start]) continue;
        let head = 0, tail = 0; queue[tail++] = start; visited[start] = 1;
        while (head < tail) {
          const index = queue[head++], x = index % size, y = Math.floor(index / size);
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
            const next = ny * size + nx;
            if (occupied[next] && !visited[next]) { visited[next] = 1; queue[tail++] = next; }
          }
        }
        if (tail > largestCount) { largestCount = tail; largest = Array.from(queue.subarray(0, tail)); }
      }
      if (largestCount < size * size * 0.02) throw new Error('silhouette-too-small');
      const occupiedCount = occupied.reduce((sum, cell) => sum + cell, 0);
      if (occupiedCount - largestCount > Math.max(8, largestCount * 0.001)) throw new Error('reference-has-disconnected-components');
      occupied.fill(0); for (const index of largest) occupied[index] = 1;
      const exterior = new Uint8Array(occupied.length); let head = 0, tail = 0;
      const enqueue = index => { if (!occupied[index] && !exterior[index]) { exterior[index] = 1; queue[tail++] = index; } };
      for (let i = 0; i < size; i++) { enqueue(i); enqueue((size - 1) * size + i); enqueue(i * size); enqueue(i * size + size - 1); }
      while (head < tail) {
        const index = queue[head++], x = index % size, y = Math.floor(index / size);
        if (x) enqueue(index - 1); if (x < size - 1) enqueue(index + 1);
        if (y) enqueue(index - size); if (y < size - 1) enqueue(index + size);
      }
      const holeVisited = new Uint8Array(occupied.length); let holes = 0;
      for (let start = 0; start < occupied.length; start++) {
        if (occupied[start] || exterior[start] || holeVisited[start]) continue;
        head = 0; tail = 0; queue[tail++] = start; holeVisited[start] = 1;
        while (head < tail) {
          const index = queue[head++], x = index % size, y = Math.floor(index / size);
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
            const next = ny * size + nx;
            if (!occupied[next] && !exterior[next] && !holeVisited[next]) { holeVisited[next] = 1; queue[tail++] = next; }
          }
        }
        if (tail >= 4) holes++;
        else for (let i = 0; i < tail; i++) occupied[queue[i]] = 1;
      }
      if (!holes && specification.allowNoVisibleHole !== true) throw new Error('reference-hole-not-visible');
      return { occupied, size, texture, holes, crop: { minX, minY, maxX, maxY } };
    }

    mesh(specification, geometry) {
      // The official print silhouette and product dimensions anchor this model.
      // Shell curvature and silver-cap dimensions are visual estimates from photos.
      const size = 192, ratio = specification.heightCm / specification.widthCm;
      const halfDepth = specification.thicknessMm / 10 / specification.widthCm;
      const occupied = new Uint8Array(size * size);
      for (let y=0; y<size; y++) for (let x=0; x<size; x++) {
        const sx=Math.min(geometry.size-1,Math.floor((x+.5)*geometry.size/size));
        const sy=Math.min(geometry.size-1,Math.floor((y+.5)*geometry.size/size));
        occupied[y*size+x]=geometry.occupied[sy*geometry.size+sx];
      }
      // Distance to silhouette edge controls the approximate rounded bulge.
      const distance=new Float32Array(size*size);
      for(let y=0;y<size;y++) for(let x=0;x<size;x++){
        const i=y*size+x;
        distance[i]=occupied[i] ? ((x===0||y===0||x===size-1||y===size-1)?1:1e4) : 0;
      }
      for(let y=1;y<size-1;y++)for(let x=1;x<size-1;x++){
        const i=y*size+x;distance[i]=Math.min(distance[i],distance[i-1]+1,distance[i-size]+1,distance[i-size-1]+1.414,distance[i-size+1]+1.414);
      }
      for(let y=size-2;y>0;y--)for(let x=size-2;x>0;x--){
        const i=y*size+x;distance[i]=Math.min(distance[i],distance[i+1]+1,distance[i+size]+1,distance[i+size-1]+1.414,distance[i+size+1]+1.414);
      }
      // A translucent rim surrounds the printed insert in the supplier photo.
      const inkMask=document.createElement('canvas');inkMask.width=size;inkMask.height=size;
      const inkContext=inkMask.getContext('2d');const inkImage=inkContext.createImageData(size,size);
      for(let i=0;i<distance.length;i++){const j=i*4;inkImage.data[j]=inkImage.data[j+1]=inkImage.data[j+2]=255;inkImage.data[j+3]=Math.round(255*Math.max(0,Math.min(1,(distance[i]-2)/2)));}
      inkContext.putImageData(inkImage,0,0);geometry.texture=inkMask;
      const at=(x,y)=>distance[Math.max(0,Math.min(size-1,y))*size+Math.max(0,Math.min(size-1,x))] || 0;
      const depth=(x,y)=>{const d=(at(x-1,y-1)+at(x,y-1)+at(x-1,y)+at(x,y))/4;return halfDepth*(.28+.72*Math.sqrt(Math.min(1,d/36)));};
      const positions=[];
      const emit=(x,y,z,n,surface)=>[x,y,z,...n,(x+1)/2,y/(2*ratio)+.5,surface];
      const tri=(a,b,c)=>positions.push(...a,...b,...c);
      const point=(x,y)=>[-1+2*x/size,ratio-2*ratio*y/size];
      const filled=(x,y)=>x>=0&&y>=0&&x<size&&y<size&&occupied[y*size+x];
      const normal=(x,y,side)=>{
        const dx=(depth(x+1,y)-depth(x-1,y))*size/4;
        const dy=(depth(x,y-1)-depth(x,y+1))*size/(4*ratio);
        const nx=-dx, ny=-dy, nz=side;const norm=Math.hypot(nx,ny,nz)||1;
        return [nx/norm,ny/norm,nz/norm];
      };
      for(let y=0;y<size;y++) for(let x=0;x<size;x++){
        if(!filled(x,y)) continue;
        const [left,top]=point(x,y),[right,bottom]=point(x+1,y+1);
        const v=[[left,bottom,x,y+1],[right,bottom,x+1,y+1],[right,top,x+1,y],[left,top,x,y]];
        const f=v.map(([px,py,cx,cy])=>emit(px,py,depth(cx,cy),normal(cx,cy,1),0));
        const b=v.map(([px,py,cx,cy])=>emit(px,py,-depth(cx,cy),normal(cx,cy,-1),1));
        tri(f[0],f[1],f[2]);tri(f[0],f[2],f[3]);tri(b[0],b[2],b[1]);tri(b[0],b[3],b[2]);
        const wall=(x0,y0,x1,y1,n)=>{const a=point(x0,y0),c=point(x1,y1),d0=depth(x0,y0),d1=depth(x1,y1);const v=(p,z)=>emit(p[0],p[1],z,n,2);tri(v(a,-d0),v(c,-d1),v(c,d1));tri(v(a,-d0),v(c,d1),v(a,d0));};
        if(!filled(x,y+1))wall(x,y+1,x+1,y+1,[0,-1,0]);
        if(!filled(x+1,y))wall(x+1,y+1,x+1,y,[1,0,0]);
        if(!filled(x,y-1))wall(x+1,y,x,y,[0,1,0]);
        if(!filled(x-1,y))wall(x,y,x,y+1,[-1,0,0]);
      }
      // Silver hanging cap: a separate opaque cylindrical mesh, not a hole.
      const capR=.16,capZ=Math.min(.15,halfDepth*.72),y0=ratio*.91,y1=ratio*1.075,segments=32;
      for(let i=0;i<segments;i++){
        const a=i*Math.PI*2/segments,b=(i+1)*Math.PI*2/segments;
        const v=(angle,y)=>emit(capR*Math.cos(angle),y,capZ*Math.sin(angle),[Math.cos(angle),0,Math.sin(angle)],3);
        const a0=v(a,y0),a1=v(a,y1),b0=v(b,y0),b1=v(b,y1);tri(a0,a1,b1);tri(a0,b1,b0);
        const center=emit(0,y1,0,[0,1,0],3),left=emit(capR*Math.cos(a),y1,capZ*Math.sin(a),[0,1,0],3),right=emit(capR*Math.cos(b),y1,capZ*Math.sin(b),[0,1,0],3);tri(center,right,left);
      }
      // Supplier photo shows a fine silver loop attached at the cap, outside
      // the 9 cm body. Its curvature and length are visual estimates.
      const cordSegments=80,cordSides=8,cordRadius=.009;
      const cord=(t,phi)=>{
        const x=-.30*(1-Math.cos(t))+.12*Math.sin(t), y=y1+.75*(1-Math.cos(t));
        const dx=-.30*Math.sin(t)+.12*Math.cos(t), dy=.75*Math.sin(t), length=Math.hypot(dx,dy)||1;
        const n=[-dy/length*Math.cos(phi),dx/length*Math.cos(phi),Math.sin(phi)];
        return emit(x+cordRadius*n[0],y+cordRadius*n[1],cordRadius*n[2],n,3);
      };
      for(let i=0;i<cordSegments;i++)for(let j=0;j<cordSides;j++){
        const t=i*Math.PI*2/cordSegments,u=(i+1)*Math.PI*2/cordSegments;
        const a=j*Math.PI*2/cordSides,b=(j+1)*Math.PI*2/cordSides;
        const p=cord(t,a),q=cord(t,b),r=cord(u,a),s=cord(u,b);
        tri(p,s,r);tri(p,q,s);
      }
      return new Float32Array(positions);
    }

    draw() {
      if (!this.gl || !this.spec || this.hidden || this.dataset.ready !== 'true') return;
      const gl = this.gl, rectangle = this.canvas.getBoundingClientRect();
      if (!rectangle.width || !rectangle.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2), width = Math.round(rectangle.width * dpr), height = Math.round(rectangle.height * dpr);
      if (this.canvas.width !== width || this.canvas.height !== height) { this.canvas.width = width; this.canvas.height = height; }
      const appearance = materials[this.spec.material];
      gl.viewport(0, 0, width, height);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.useProgram(this.program);
      gl.uniform2f(this.uniform.uAngles, this.yaw, this.pitch);
      const aspect = width / height;
      const fit = Math.max(1, this.spec.heightCm / this.spec.widthCm, 1 / aspect);
      gl.uniform1f(this.uniform.uDistance, 3 * fit / this.zoom);
      gl.uniform1f(this.uniform.uAspect, aspect);
      gl.uniform3fv(this.uniform.uFront, appearance.front);
      gl.uniform3fv(this.uniform.uBack, appearance.back);
      gl.uniform3fv(this.uniform.uEdge, appearance.edge);
      gl.uniform1f(this.uniform.uShine, appearance.shine);
      gl.uniform3fv(this.uniform.uOpacity, [this.spec.opticalAppearance.frontOpacity, this.spec.opticalAppearance.backOpacity, this.spec.opticalAppearance.edgeOpacity]);
      gl.enable(gl.BLEND);
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      for (const culledFace of [gl.FRONT, gl.BACK]) {
        gl.clear(gl.DEPTH_BUFFER_BIT);
        gl.cullFace(culledFace);
        gl.depthMask(true);
        gl.colorMask(false, false, false, false);
        gl.depthFunc(gl.LESS);
        gl.drawArrays(gl.TRIANGLES, 0, this.count);
        gl.depthMask(false);
        gl.colorMask(true, true, true, true);
        gl.depthFunc(gl.LEQUAL);
        gl.enable(gl.POLYGON_OFFSET_FILL);
        gl.polygonOffset(-2, -2);
        gl.drawArrays(gl.TRIANGLES, 0, this.count);
        gl.disable(gl.POLYGON_OFFSET_FILL);
      }
      gl.depthMask(true);
      gl.depthFunc(gl.LESS);
      gl.cullFace(gl.BACK);
      gl.disable(gl.BLEND);
      this.dataset.yaw = String(this.yaw);
      this.dataset.pitch = String(this.pitch);
      this.dataset.zoom = String(this.zoom);
      this.dispatchEvent(new CustomEvent('ornament-model-render', { bubbles: true, detail: { referenceOnly: true } }));
    }
  }
  if (!customElements.get('venro-ornament-plastic-star-3d')) customElements.define('venro-ornament-plastic-star-3d', VenroOrnamentPlasticStar3D);
})();
